import { captureException } from "@sentry/react";
import { AlertCircle, Reply, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "zustand";
import { ProfileOrganization } from "@/components/app/profile-organization";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FileAttachmentList } from "@/components/ui/file-attachment";
import { TiptapEditor } from "@/components/ui/tiptap-editor";
import { UploadProgress } from "@/components/ui/upload-progress";
import { type UploadedFile, useFileUpload } from "@/hooks/use-file-upload";
import { useUser } from "@/hooks/use-user";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { uiStore } from "@/lib/stores/ui-store";
import type { MentionRole, MentionUser } from "@/lib/tiptap-config";
import { useChatDropzone } from "./chat-dropzone";

type MessageInputProps = {
	onSendMessage: (
		richContent: Record<string, unknown>,
		attachments?: UploadedFile[]
	) => void;
	placeholder?: string;
	replyingTo?: {
		id: string;
		authorId: string;
		richContent: Record<string, unknown>;
	} | null;
	onCancelReply?: () => void;
	onTyping?: () => void;
	users?: Omit<MentionUser, "type">[];
	roles?: Omit<MentionRole, "type">[];
	channelId: string;
	authorId: string;
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_FILES = 10;

export function MessageInput({
	onSendMessage,
	placeholder = "Message...",
	replyingTo,
	onCancelReply,
	onTyping,
	users = [],
	roles = [],
	channelId,
	authorId,
}: MessageInputProps) {
	const { getUserDisplayName } = useUser();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const draft = useStore(uiStore, (s) => s.drafts.get(channelId));
	const setDraft = useStore(uiStore, (s) => s.setDraft);
	const deleteDraft = useStore(uiStore, (s) => s.deleteDraft);
	const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const handleContentChange = useCallback(
		(json: Record<string, unknown>) => {
			if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
			draftTimerRef.current = setTimeout(() => {
				const content = json.content as unknown[] | undefined;
				const isEmpty =
					!content ||
					content.length === 0 ||
					(content.length === 1 &&
						(content[0] as Record<string, unknown>).type === "paragraph" &&
						!(content[0] as Record<string, unknown>).content);
				if (isEmpty) {
					deleteDraft(channelId);
				} else {
					setDraft(channelId, json);
				}
			}, 300);
		},
		[channelId, setDraft, deleteDraft]
	);

	const {
		upload,
		control,
		pendingFiles,
		isUploading,
		clearPendingFiles,
		removePendingFile,
	} = useFileUpload({
		channelId,
		authorId,
		onUploadComplete: () => {
			setUploadError(null);
		},
		onError: (error) => {
			const isNetworkError =
				error.message.includes("fetch") ||
				error.message.includes("network") ||
				error.message.includes("Failed to upload");
			setUploadError(
				isNetworkError
					? "Upload failed. Please check your connection and try again by clicking the attach button."
					: error.message
			);
		},
	});

	const handleFilesUpload = useCallback(
		async (files: File[]) => {
			if (files.length === 0) return;

			setUploadError(null);

			const totalFiles = pendingFiles.length + files.length;
			if (totalFiles > MAX_FILES) {
				setUploadError(
					`You can only upload up to ${MAX_FILES} files at once. Currently ${pendingFiles.length} file(s) attached.`
				);
				return;
			}

			const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);
			if (oversizedFiles.length > 0) {
				const fileNames = oversizedFiles.map((f) => f.name).join(", ");
				setUploadError(
					`File(s) too large: ${fileNames}. Max size is 50MB per file.`
				);
				return;
			}

			try {
				await upload(files);
			} catch (error) {
				if (SENTRY_ENABLED)
					captureException(error, { tags: { context: "message_upload" } });
				setUploadError(
					error instanceof Error ? error.message : "Failed to upload files"
				);
			}
		},
		[pendingFiles.length, upload]
	);

	const dropzone = useChatDropzone();

	useEffect(() => {
		if (dropzone) {
			dropzone.setOnFilesDropped(handleFilesUpload);
		}
	}, [dropzone, handleFilesUpload]);

	const handleEditorSubmit = (
		_html: string,
		richContent: Record<string, unknown>
	) => {
		onSendMessage(
			richContent,
			pendingFiles.length > 0 ? pendingFiles : undefined
		);
		clearPendingFiles();
		deleteDraft(channelId);
	};

	const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;

		await handleFilesUpload(Array.from(files));

		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleAttachClick = () => {
		fileInputRef.current?.click();
	};

	const pendingFilesAsAttachments = pendingFiles.map((f) => ({
		fileName: f.fileName,
		fileSize: f.fileSize,
		fileType: f.fileType,
		url: f.url,
		objectKey: f.objectKey,
	}));

	return (
		<div
			className="overflow-hidden rounded-lg border bg-background"
			data-tour="message-input"
		>
			{replyingTo && (
				<div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
					<Reply className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
					<div className="min-w-0 flex-1">
						<div className="text-muted-foreground text-xs">
							Replying to{" "}
							<span className="font-semibold">
								{getUserDisplayName(replyingTo.authorId)}
							</span>
							<ProfileOrganization className="ml-1" uid={replyingTo.authorId} />
						</div>
						<div className="line-clamp-1 text-muted-foreground text-xs">
							{(
								replyingTo.richContent?.content as Array<{
									content?: Array<{ text?: string }>;
								}>
							)?.[0]?.content?.[0]?.text || "[Message]"}
						</div>
					</div>
					<Button
						className="size-6"
						onClick={onCancelReply}
						size="icon"
						title="Cancel reply"
						variant="ghost"
					>
						<X className="size-3" />
					</Button>
				</div>
			)}

			{uploadError && (
				<div className="border-b bg-destructive/10 p-3">
					<div className="relative">
						<Alert variant="destructive">
							<AlertCircle className="size-4" />
							<AlertDescription className="ml-2 pr-8">
								{uploadError}
							</AlertDescription>
						</Alert>
						<Button
							className="absolute top-2 right-2 size-6"
							onClick={() => setUploadError(null)}
							size="icon"
							variant="ghost"
						>
							<X className="size-3" />
						</Button>
					</div>
				</div>
			)}

			{isUploading && (
				<div className="border-b bg-muted/20 p-3">
					<UploadProgress control={control} />
				</div>
			)}

			{pendingFiles.length > 0 && (
				<div className="border-b bg-muted/20 p-3">
					<div className="mb-2 flex items-center justify-between">
						<span className="font-medium text-muted-foreground text-xs">
							{pendingFiles.length} / {MAX_FILES} file(s)
						</span>
						<Button
							className="h-6 text-xs"
							onClick={clearPendingFiles}
							size="sm"
							variant="ghost"
						>
							Clear all
						</Button>
					</div>
					<div className="max-h-[300px] overflow-y-auto">
						<FileAttachmentList
							files={pendingFilesAsAttachments}
							onRemove={(index) => {
								const file = pendingFiles[index];
								if (file) {
									removePendingFile(file.objectKey);
								}
							}}
							showRemove
						/>
					</div>
				</div>
			)}

			<div className="relative">
				<TiptapEditor
					editorClassName="max-h-[200px] py-3"
					initialContent={draft}
					isUploading={isUploading}
					key={channelId}
					onAttachClick={handleAttachClick}
					onContentChange={handleContentChange}
					onSubmit={handleEditorSubmit}
					onTyping={onTyping}
					placeholder={placeholder}
					roles={roles}
					users={users}
				/>
				<input
					accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar"
					className="hidden"
					multiple
					onChange={handleFileSelect}
					ref={fileInputRef}
					type="file"
				/>
			</div>
		</div>
	);
}
